import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return setError("Password must be at least 6 characters.");
    if (pw !== pw2) return setError("Passwords don't match.");
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please log in.");
      nav("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="container-fl py-16 max-w-md" data-testid="reset-password-invalid">
        <h1 className="text-3xl font-black tracking-tighter mb-2">Invalid reset link</h1>
        <p className="text-neutral-500 text-sm mb-8">
          This link is missing its token. Please request a new one.
        </p>
        <Link to="/forgot-password" className="text-emerald-700 font-semibold">
          Request a new reset link
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fl py-16 max-w-md" data-testid="reset-password-page">
      <h1 className="text-3xl font-black tracking-tighter mb-2">Choose a new password</h1>
      <p className="text-neutral-500 text-sm mb-8">
        Pick something at least 6 characters. You'll be logged out of any old sessions.
      </p>

      <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">
            New password
          </label>
          <input
            data-testid="reset-password-new"
            type="password"
            required
            minLength={6}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="mt-1 h-12 w-full rounded-md border border-neutral-200 px-3 outline-none focus:border-neutral-900"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">
            Confirm new password
          </label>
          <input
            data-testid="reset-password-confirm"
            type="password"
            required
            minLength={6}
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="mt-1 h-12 w-full rounded-md border border-neutral-200 px-3 outline-none focus:border-neutral-900"
          />
        </div>
        {error && (
          <div data-testid="reset-password-error" className="text-sm text-red-600">
            {error}
          </div>
        )}
        <button
          data-testid="reset-password-submit"
          type="submit"
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
